import type { Permissions, RolePermissions, Unit, User } from '../../core/models/data';

export interface LeaveRequestUnitPermission {
  unitId: string;
  canView: boolean;
  canViewTeam: boolean;
  canCreate: boolean;
  canViewAll: boolean;
  canReview: boolean;
  canAccessAdminList: boolean;
  canSeeNotes: boolean;
  canAccess: boolean;
}

export interface LeaveRequestPermissionScope {
  byUnitId: Record<string, LeaveRequestUnitPermission>;
  accessibleUnitIds: string[];
  createUnitIds: string[];
  viewAllUnitIds: string[];
  teamVisibleUnitIds: string[];
  reviewUnitIds: string[];
  adminListUnitIds: string[];
  noteUnitIds: string[];
  canAccessAny: boolean;
}

const getCandidateUnitIds = (currentUser: User, allUnits: Unit[], activeUnitIds?: string[]): string[] => {
  if (activeUnitIds?.length) {
    return Array.from(new Set(activeUnitIds.filter(unitId => unitId && hasUnitScope(currentUser, unitId, allUnits))));
  }

  if (currentUser.role === 'Admin') {
    const allUnitIds = allUnits.map(unit => unit.id).filter(Boolean);
    return allUnitIds.length ? allUnitIds : (currentUser.unitIds || []).filter(Boolean);
  }

  return Array.from(new Set((currentUser.unitIds || []).filter(Boolean)));
};

const hasUnitScope = (currentUser: User, unitId: string, allUnits: Unit[]): boolean => {
  if (currentUser.role === 'Admin') return true;
  return (currentUser.unitIds || []).includes(unitId);
};

const resolvePermissionForUnit = ({
  currentUser,
  unitId,
  permission,
  allPermissions,
  unitPermissions,
  allUnits,
}: {
  currentUser: User;
  unitId: string;
  permission: keyof Permissions;
  allPermissions: RolePermissions;
  unitPermissions: Record<string, any>;
  allUnits: Unit[];
}): boolean => {
  if (currentUser.role === 'Admin') return true;
  if (!unitId || !hasUnitScope(currentUser, unitId, allUnits)) return false;

  const unitDecision = unitPermissions[unitId]?.roles?.[currentUser.role]?.[permission];
  if (unitDecision === true) return true;
  if (unitDecision === false) return false;

  return Boolean(allPermissions[currentUser.role]?.[permission]);
};

export const resolveLeaveRequestUnitPermission = ({
  currentUser,
  unitId,
  allPermissions,
  unitPermissions,
  allUnits,
}: {
  currentUser: User;
  unitId: string;
  allPermissions: RolePermissions;
  unitPermissions: Record<string, any>;
  allUnits: Unit[];
}): LeaveRequestUnitPermission => {
  const has = (permission: keyof Permissions) =>
    resolvePermissionForUnit({
      currentUser,
      unitId,
      permission,
      allPermissions,
      unitPermissions,
      allUnits,
    });

  const legacyCreate = has('canSubmitLeaveRequests');
  const legacyManage = has('canManageLeaveRequests');
  const canCreate = has('canCreateOwnLeaveRequests') || legacyCreate;
  const canViewTeam = has('canViewTeamLeaveRequests');
  const canReview = has('canReviewLeaveRequests') || legacyManage;
  const canAccessAdminList = has('canAccessLeaveRequestAdminList') || canReview || legacyManage;
  const canViewAll = has('canViewAllLeaveRequests') || canReview || canAccessAdminList || legacyManage;
  const canView =
    has('canViewLeaveRequests') ||
    legacyManage ||
    canViewAll ||
    canReview ||
    canAccessAdminList;
  const canSeeNotes = canViewAll || canReview || canAccessAdminList;

  return {
    unitId,
    canView,
    canViewTeam,
    canCreate,
    canViewAll,
    canReview,
    canAccessAdminList,
    canSeeNotes,
    canAccess: canView || canViewTeam || canCreate || canViewAll || canReview || canAccessAdminList,
  };
};

export const resolveLeaveRequestPermissionScope = ({
  currentUser,
  allPermissions,
  unitPermissions,
  allUnits,
  activeUnitIds,
}: {
  currentUser: User;
  allPermissions: RolePermissions;
  unitPermissions: Record<string, any>;
  allUnits: Unit[];
  activeUnitIds?: string[];
}): LeaveRequestPermissionScope => {
  const candidateUnitIds = getCandidateUnitIds(currentUser, allUnits, activeUnitIds);
  const byUnitId = Object.fromEntries(
    candidateUnitIds.map(unitId => [
      unitId,
      resolveLeaveRequestUnitPermission({
        currentUser,
        unitId,
        allPermissions,
        unitPermissions,
        allUnits,
      }),
    ])
  );

  const values = Object.values(byUnitId);
  const unitIdsWhere = (predicate: (permission: LeaveRequestUnitPermission) => boolean) =>
    values.filter(predicate).map(permission => permission.unitId);

  const accessibleUnitIds = unitIdsWhere(permission => permission.canAccess);

  return {
    byUnitId,
    accessibleUnitIds,
    createUnitIds: unitIdsWhere(permission => permission.canCreate),
    viewAllUnitIds: unitIdsWhere(permission => permission.canViewAll),
    teamVisibleUnitIds: unitIdsWhere(permission => permission.canViewTeam),
    reviewUnitIds: unitIdsWhere(permission => permission.canReview),
    adminListUnitIds: unitIdsWhere(permission => permission.canAccessAdminList),
    noteUnitIds: unitIdsWhere(permission => permission.canSeeNotes),
    canAccessAny: currentUser.role === 'Admin' || accessibleUnitIds.length > 0,
  };
};

export const chunkFirestoreInValues = (values: string[], chunkSize = 10): string[][] => {
  const uniqueIds = Array.from(new Set(values.filter(Boolean)));
  const chunks: string[][] = [];
  for (let index = 0; index < uniqueIds.length; index += chunkSize) {
    chunks.push(uniqueIds.slice(index, index + chunkSize));
  }
  return chunks;
};
